import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { KeyRound, AlertCircle, CheckCircle, Eye, EyeOff, LogIn } from 'lucide-react'
import PasswordStrengthIndicator from '../components/auth/PasswordStrengthIndicator'
import api, { handleApiError } from '../services/api'

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'הסיסמה חייבת להכיל לפחות 8 תווים')
      .regex(/[A-Za-z֐-׿]/, 'הסיסמה חייבת להכיל לפחות אות אחת')
      .regex(/[0-9]/, 'הסיסמה חייבת להכיל לפחות ספרה אחת'),
    confirmPassword: z.string().min(1, 'שדה חובה'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'הסיסמאות אינן תואמות',
    path: ['confirmPassword'],
  })

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  })

  const password = watch('password')

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setError('קישור איפוס הסיסמה אינו תקין — לא נמצא אסימון.')
      return
    }
    setIsLoading(true)
    setError(null)

    try {
      await api.post('/auth/reset-password', {
        token,
        password: data.password,
        confirmPassword: data.confirmPassword,
      })
      setSuccess(true)
      setTimeout(() => navigate('/login'), 3000)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-bl from-primary-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="card text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <AlertCircle size={32} className="text-red-600" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              קישור לא תקין
            </h2>
            <p className="text-gray-600 mb-6">
              קישור איפוס הסיסמה אינו תקין או שפג תוקפו. אנא בקש/י קישור חדש.
            </p>
            <Link to="/forgot-password" className="btn btn-primary">
              בקשת קישור חדש
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-bl from-primary-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="card text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircle size={32} className="text-green-600" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              הסיסמה אופסה בהצלחה! ✅
            </h2>
            <p className="text-gray-600 mb-6">
              ניתן להתחבר כעת עם הסיסמה החדשה. מועבר/ת לעמוד ההתחברות...
            </p>
            <Link to="/login" className="btn btn-primary inline-flex items-center gap-2">
              <LogIn size={18} aria-hidden="true" />
              <span>מעבר להתחברות</span>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-bl from-primary-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-heading font-bold text-primary-600 mb-2">
            יפתח ונגר יועץ השקעות
          </h1>
          <p className="text-gray-600">בחירת סיסמה חדשה</p>
        </div>

        <div className="card">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
            איפוס סיסמה
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            בחר/י סיסמה חדשה חזקה לחשבון שלך.
          </p>

          {error && (
            <div className="alert alert-error mb-6" role="alert">
              <AlertCircle size={20} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                סיסמה חדשה <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  className={`input ${errors.password ? 'input-error' : ''}`}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  dir="ltr"
                  aria-required="true"
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? 'הסתרת סיסמה' : 'הצגת סיסמה'}
                >
                  {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                </button>
              </div>
              {errors.password && (
                <p id="password-error" role="alert" className="text-sm text-red-600 mt-1">{errors.password.message}</p>
              )}
              <PasswordStrengthIndicator password={password || ''} />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                אימות סיסמה חדשה <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  {...register('confirmPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  className={`input ${errors.confirmPassword ? 'input-error' : ''}`}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  dir="ltr"
                  aria-required="true"
                  aria-invalid={!!errors.confirmPassword}
                  aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showConfirmPassword ? 'הסתרת סיסמה' : 'הצגת סיסמה'}
                >
                  {showConfirmPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p id="confirmPassword-error" role="alert" className="text-sm text-red-600 mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="spinner" />
                  <span>מאפס סיסמה...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <KeyRound size={20} aria-hidden="true" />
                  <span>אפס סיסמה</span>
                </div>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              חזרה להתחברות
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
